import React, { useState, useEffect } from "react";
import {
  getTeacherAvailability,
  updateTeacherAvailability,
} from "../../../server/Teacher-dashboard/requests";
import { getProfile } from "server/Authentication/Requests";
import { Profile } from "server/Authentication/OutputsTypes";
// Couleur jaune de l'application
const YELLOW = "#FFBB28";

const AvailabilityToggle: React.FC = () => {
  const [availability, setAvailability] = useState<{
    available: boolean;
    reason: string;
  }>({ available: false, reason: "" });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  useEffect(() => {
    fetchProfileAndAvailability();
  }, []);

  const fetchProfileAndAvailability = async () => {
    try {
      const profileResponse = await getProfile();
      if (profileResponse.status === 200 && profileResponse.body) {
        const profile = profileResponse.body as Profile;
        if (profile.availability) {
          setAvailability({
            available: profile.availability.available,
            reason: profile.availability.reason,
          });
          setReason(profile.availability.reason);
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du profil:", error);
    }
  };

  const handleToggle = async () => {
    if (availability.available) {
      // Si on est disponible et qu'on veut passer à indisponible, on ouvre le modal
      setShowModal(true);
    } else {
      // Si on est indisponible et qu'on veut passer à disponible, on met à jour directement
      await updateAvailability(true, "");
    }
  };

  const updateAvailability = async (isAvailable: boolean, reason: string) => {
    setLoading(true);
    try {
      const response = await updateTeacherAvailability({
        available: isAvailable,
        reason: reason,
      });

      if (response.status === 200) {
        // Mise à jour de l'état uniquement si on reçoit un 200
        setAvailability({
          available: isAvailable,
          reason: reason,
        });
        setReason(reason);
        setShowModal(false);

        // Afficher le message de succès
        setToastMsg(
          isAvailable
            ? "Vous êtes maintenant disponible."
            : "Vous êtes maintenant indisponible.",
        );
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2500);
      } else {
        // En cas d'erreur non-200
        setToastMsg(
          "Une erreur est survenue lors de la mise à jour de votre disponibilité.",
        );
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2500);
      }
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de la disponibilité:",
        error,
      );
      setShowModal(false);
      setToastMsg(
        "Une erreur est survenue lors de la mise à jour de votre disponibilité.",
      );
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toast */}
      {showToast && (
        <div
          className="fixed top-6 right-6 z-[99999] bg-white border border-gray-200 shadow-lg px-6 py-3 rounded text-gray-900 flex items-center"
          style={{ background: YELLOW, color: "#222", fontWeight: 600 }}
        >
          {toastMsg}
        </div>
      )}
      <div className="flex items-center space-x-4">
        <span className="text-gray-700 font-medium">
          {availability.available ? "Disponible" : "Non disponible"}
        </span>
        <button
          onClick={handleToggle}
          disabled={loading}
          aria-label="Changer la disponibilité"
          className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
          style={{
            background: availability.available ? YELLOW : "#eee",
            border: `2px solid ${YELLOW}`,
          }}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
              availability.available ? "translate-x-7" : "translate-x-1"
            }`}
            style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
          />
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.25)" }}
        >
          <div className="bg-white rounded-lg p-6 max-w-md w-full z-50 shadow-xl border border-gray-200">
            <h3 className="text-lg font-medium mb-4">
              Définir votre indisponibilité
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Raison de votre indisponibilité
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-400 focus:border-yellow-400"
                rows={3}
                placeholder="Ex: Indisponible jusqu'à 18h pour cause de réunion"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                onClick={() => updateAvailability(false, reason)}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                style={{ background: YELLOW, color: "#222", fontWeight: 600 }}
                disabled={loading}
              >
                {loading ? "Mise à jour..." : "Confirmer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AvailabilityToggle;
